import React from "react";
import { MapPin, Phone, Mail, Clock, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { CLINIC } from "@/lib/content";

export default function Contact() {
    const [form, setForm] = React.useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });
    const [submitting, setSubmitting] = React.useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = { ...form };
            if (!payload.phone) delete payload.phone;
            if (!payload.subject) delete payload.subject;
            await api.post("/contact", payload);
            toast.success("Message sent — thank you.", {
                description: "Our team will reply within one working day.",
            });
            setForm({
                name: "",
                email: "",
                phone: "",
                subject: "",
                message: "",
            });
        } catch (err) {
            toast.error(
                formatApiError(err?.response?.data?.detail) ||
                    "Could not send message.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="py-24 md:py-32"
        >
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <div className="max-w-2xl mb-14">
                    <div className="text-xs uppercase tracking-[0.3em] text-[#7a8582] flex items-center">
                        <span className="gdc-divider" />
                        Visit us
                    </div>
                    <h2 className="font-serif-display mt-4 text-4xl md:text-5xl leading-tight text-[#1a1d1c]">
                        Visit us in the
                        <br />
                        <span className="italic text-[#114b5f]">
                            heart of Udaipur.
                        </span>
                    </h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-5 space-y-6">
                        <div className="rounded-3xl overflow-hidden aspect-[4/3] bg-[#e8e4db]">
                            <iframe
                                title="Galaxy Dental Clinic Location"
                                src={CLINIC.mapEmbed}
                                width="100%"
                                height="100%"
                                loading="lazy"
                                className="grayscale hover:grayscale-0 transition-all duration-700"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>
                        <ul className="grid grid-cols-1 gap-4 text-sm text-[#4a5250]">
                            <li className="flex gap-3">
                                <MapPin
                                    size={18}
                                    strokeWidth={1.5}
                                    className="text-[#114b5f] shrink-0 mt-0.5"
                                />
                                <span>
                                    {CLINIC.address}
                                    <br />
                                    <a
                                        href={CLINIC.mapsLink}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid="contact-directions-link"
                                        className="inline-block mt-1 text-[#114b5f] underline underline-offset-4 hover:text-[#0d3a4a]"
                                    >
                                        Get directions on Google Maps →
                                    </a>
                                </span>
                            </li>
                            <li className="flex gap-3">
                                <Phone
                                    size={18}
                                    strokeWidth={1.5}
                                    className="text-[#114b5f] shrink-0 mt-0.5"
                                />
                                <a
                                    href={`tel:${CLINIC.phoneRaw}`}
                                    data-testid="contact-phone"
                                >
                                    {CLINIC.phone}
                                </a>
                            </li>
                            <li className="flex gap-3">
                                <Mail
                                    size={18}
                                    strokeWidth={1.5}
                                    className="text-[#114b5f] shrink-0 mt-0.5"
                                />
                                <a
                                    href={`mailto:${CLINIC.email}`}
                                    data-testid="contact-email"
                                >
                                    {CLINIC.email}
                                </a>
                            </li>
                            <li className="flex gap-3">
                                <Clock
                                    size={18}
                                    strokeWidth={1.5}
                                    className="text-[#114b5f] shrink-0 mt-0.5"
                                />
                                <span>{CLINIC.hours}</span>
                            </li>
                        </ul>
                    </div>

                    <form
                        onSubmit={submit}
                        data-testid="contact-form"
                        className="lg:col-span-7 bg-[#f3f0ea] rounded-3xl p-8 md:p-10 space-y-5"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label
                                    htmlFor="ct-name"
                                    className="text-xs uppercase tracking-wider text-[#4a5250]"
                                >
                                    Name
                                </Label>
                                <Input
                                    id="ct-name"
                                    data-testid="contact-name-input"
                                    value={form.name}
                                    onChange={(e) =>
                                        update("name", e.target.value)
                                    }
                                    required
                                    className="mt-1 bg-white rounded-xl border-[#e8e4db]"
                                />
                            </div>
                            <div>
                                <Label
                                    htmlFor="ct-email"
                                    className="text-xs uppercase tracking-wider text-[#4a5250]"
                                >
                                    Email
                                </Label>
                                <Input
                                    id="ct-email"
                                    data-testid="contact-email-input"
                                    type="email"
                                    value={form.email}
                                    onChange={(e) =>
                                        update("email", e.target.value)
                                    }
                                    required
                                    className="mt-1 bg-white rounded-xl border-[#e8e4db]"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label
                                    htmlFor="ct-phone"
                                    className="text-xs uppercase tracking-wider text-[#4a5250]"
                                >
                                    Phone
                                </Label>
                                <Input
                                    id="ct-phone"
                                    data-testid="contact-phone-input"
                                    value={form.phone}
                                    onChange={(e) =>
                                        update("phone", e.target.value)
                                    }
                                    className="mt-1 bg-white rounded-xl border-[#e8e4db]"
                                />
                            </div>
                            <div>
                                <Label
                                    htmlFor="ct-subject"
                                    className="text-xs uppercase tracking-wider text-[#4a5250]"
                                >
                                    Subject
                                </Label>
                                <Input
                                    id="ct-subject"
                                    data-testid="contact-subject-input"
                                    value={form.subject}
                                    onChange={(e) =>
                                        update("subject", e.target.value)
                                    }
                                    className="mt-1 bg-white rounded-xl border-[#e8e4db]"
                                />
                            </div>
                        </div>
                        <div>
                            <Label
                                htmlFor="ct-msg"
                                className="text-xs uppercase tracking-wider text-[#4a5250]"
                            >
                                Message
                            </Label>
                            <Textarea
                                id="ct-msg"
                                data-testid="contact-message-input"
                                value={form.message}
                                onChange={(e) =>
                                    update("message", e.target.value)
                                }
                                required
                                rows={5}
                                className="mt-1 bg-white rounded-xl border-[#e8e4db]"
                            />
                        </div>
                        <button
                            type="submit"
                            data-testid="contact-submit"
                            disabled={submitting}
                            className="rounded-full bg-[#114b5f] text-[#faf9f6] px-8 py-4 text-sm font-medium hover:bg-[#0d3a4a] transition disabled:opacity-60 flex items-center gap-2"
                        >
                            {submitting ? (
                                <>
                                    <Loader2
                                        className="animate-spin"
                                        size={16}
                                    />
                                    Sending…
                                </>
                            ) : (
                                <>
                                    Send message
                                    <Send size={14} strokeWidth={1.75} />
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </section>
    );
}
